import { useState, useEffect, useCallback } from 'react';
import {
  Star,
  Search,
  ArrowLeft,
  MessageSquare,
  Plus,
  X,
  ChevronDown,
  ChevronRight,
  Shield,
  GitMerge,
  Info,
} from 'lucide-react';
import {
  listIdeas,
  getIdeaDetail,
  createIdea,
  rateIdea,
  addIdeaComment,
  updateIdea,
  mergeIdea,
} from '../api/client.js';
import type { IdeaListItem, IdeaDetail } from '../api/client.js';
import { useAuth } from '../hooks/useAuth.js';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CATEGORIES = ['quotes', 'catalogs', 'resellers', 'pdf-export', 'dashboard', 'integrations', 'other'];
const CATEGORY_LABELS: Record<string, string> = {
  quotes: 'Quotes',
  catalogs: 'Catalogs',
  resellers: 'Resellers',
  'pdf-export': 'PDF Export',
  dashboard: 'Dashboard',
  integrations: 'Integrations',
  other: 'Other',
};
const STATUSES = ['new', 'under-review', 'planned', 'in-progress', 'completed', 'declined'];
const STATUS_LABELS: Record<string, string> = {
  new: 'New',
  'under-review': 'Under Review',
  planned: 'Planned',
  'in-progress': 'In Progress',
  completed: 'Completed',
  declined: 'Declined',
};

const STATUS_COLORS: Record<string, string> = {
  new: 'bg-gray-700 text-gray-300',
  'under-review': 'bg-blue-900/40 text-blue-400',
  planned: 'bg-purple-900/40 text-purple-400',
  'in-progress': 'bg-yellow-900/40 text-yellow-400',
  completed: 'bg-green-900/40 text-green-400',
  declined: 'bg-red-900/40 text-red-400',
  merged: 'bg-gray-800 text-gray-500',
};

const CATEGORY_COLORS: Record<string, string> = {
  quotes: 'bg-brand-600/20 text-brand-400',
  catalogs: 'bg-cyan-900/40 text-cyan-400',
  resellers: 'bg-violet-900/40 text-violet-400',
  'pdf-export': 'bg-pink-900/40 text-pink-400',
  dashboard: 'bg-amber-900/40 text-amber-400',
  integrations: 'bg-teal-900/40 text-teal-400',
  other: 'bg-gray-700 text-gray-300',
};

const RATING_LABELS: Record<number, string> = {
  1: 'Nice to have',
  2: 'Would be useful',
  3: 'I want this',
  4: 'I really need this',
  5: 'Critical for my business',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

// ---------------------------------------------------------------------------
// Star Rating Component
// ---------------------------------------------------------------------------

function StarRating({
  value,
  interactive = false,
  onRate,
  size = 18,
}: {
  value: number;
  interactive?: boolean;
  onRate?: (rating: number) => void;
  size?: number;
}) {
  const [hoverValue, setHoverValue] = useState<number | null>(null);
  const [animating, setAnimating] = useState<number | null>(null);
  const displayValue = hoverValue ?? value;

  const handleClick = (star: number) => {
    if (!interactive || !onRate) return;
    setAnimating(star);
    onRate(star);
    setTimeout(() => setAnimating(null), 300);
  };

  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => {
        const filled = displayValue >= star;
        const half = !filled && displayValue >= star - 0.5;

        return (
          <button
            key={star}
            type="button"
            disabled={!interactive}
            onClick={() => handleClick(star)}
            onMouseEnter={() => interactive && setHoverValue(star)}
            onMouseLeave={() => interactive && setHoverValue(null)}
            className={`transition-transform duration-150 ${
              animating === star ? 'scale-125' : ''
            } ${interactive ? 'cursor-pointer hover:scale-110' : 'cursor-default'}`}
            title={interactive ? RATING_LABELS[star] : undefined}
          >
            <Star
              size={size}
              className={
                filled
                  ? 'fill-yellow-400 text-yellow-400'
                  : half
                    ? 'fill-yellow-400/50 text-yellow-400'
                    : 'text-gray-600'
              }
            />
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Rating Distribution Bars
// ---------------------------------------------------------------------------

function RatingDistribution({
  distribution,
  total,
}: {
  distribution: Record<number, number>;
  total: number;
}) {
  return (
    <div className="space-y-1">
      {[5, 4, 3, 2, 1].map((star) => {
        const count = distribution[star] ?? 0;
        const pct = total > 0 ? (count / total) * 100 : 0;
        return (
          <div key={star} className="flex items-center gap-2 text-sm">
            <span className="w-3 text-right text-gray-400">{star}</span>
            <Star size={12} className="fill-yellow-400 text-yellow-400" />
            <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray-800">
              <div
                className="h-full rounded-full bg-yellow-400 transition-all duration-300"
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="w-6 text-right text-xs text-gray-500">{count}</span>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Submit Idea Modal
// ---------------------------------------------------------------------------

function SubmitIdeaModal({
  onClose,
  onSubmit,
}: {
  onClose: () => void;
  onSubmit: (data: { title: string; description: string; category: string }) => Promise<void>;
}) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState(CATEGORIES[0]!);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleSubmit = async () => {
    if (!title.trim() || !description.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      await onSubmit({ title: title.trim(), description: description.trim(), category });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit idea');
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="mx-4 w-full max-w-lg rounded-lg border border-gray-700 bg-gray-900 shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-700 px-6 py-4">
          <h3 className="text-lg font-semibold">Submit an Idea</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-200">
            <X size={20} />
          </button>
        </div>

        <div className="space-y-4 px-6 py-4">
          {error && (
            <div className="rounded-md bg-red-900/30 px-4 py-2 text-sm text-red-400">{error}</div>
          )}

          <div>
            <label className="mb-1 block text-sm text-gray-400">
              Title <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value.slice(0, 200))}
              placeholder="A short, descriptive title for your idea"
              className="w-full rounded-md border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-100 placeholder-gray-500 focus:border-brand-500 focus:outline-none"
            />
            <div className="mt-1 text-right text-xs text-gray-600">{title.length}/200</div>
          </div>

          <div>
            <label className="mb-1 block text-sm text-gray-400">
              Description <span className="text-red-400">*</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe your idea, the problem it solves, and how you'd use it..."
              rows={4}
              className="w-full rounded-md border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-100 placeholder-gray-500 focus:border-brand-500 focus:outline-none"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm text-gray-400">
              Category <span className="text-red-400">*</span>
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full rounded-md border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-100 focus:border-brand-500 focus:outline-none"
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {CATEGORY_LABELS[c]}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t border-gray-700 px-6 py-4">
          <button
            onClick={onClose}
            className="rounded-md px-4 py-2 text-sm text-gray-400 hover:text-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !title.trim() || !description.trim()}
            className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {submitting ? 'Submitting...' : 'Submit Idea'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Idea Card (list view)
// ---------------------------------------------------------------------------

function IdeaCard({
  idea,
  onSelect,
  onRate,
}: {
  idea: IdeaListItem;
  onSelect: (id: string) => void;
  onRate: (ideaId: string, rating: number) => void;
}) {
  return (
    <div className="flex gap-4 rounded-lg border border-gray-800 bg-gray-900 p-4 transition-shadow hover:shadow-lg hover:shadow-black/20">
      {/* Star rating column */}
      <div className="flex flex-shrink-0 flex-col items-center gap-1 pt-1">
        <StarRating
          value={idea.ratingAverage}
          interactive
          onRate={(r) => onRate(idea.id, r)}
          size={16}
        />
        <span className="text-xs text-gray-500">
          {idea.ratingAverage.toFixed(1)} avg
        </span>
        <span className="text-xs text-gray-600">{idea.ratingCount} ratings</span>
        {idea.myRating && (
          <span className="mt-0.5 text-xs text-yellow-400/70">
            Yours: {idea.myRating}/5
          </span>
        )}
      </div>

      {/* Content column */}
      <div className="flex-1 cursor-pointer" onClick={() => onSelect(idea.id)}>
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-medium text-gray-200 hover:text-white">{idea.title}</h3>
          <div className="flex flex-shrink-0 gap-2">
            <span
              className={`rounded-full px-2 py-0.5 text-xs font-medium ${CATEGORY_COLORS[idea.category] || CATEGORY_COLORS.other}`}
            >
              {CATEGORY_LABELS[idea.category] || idea.category}
            </span>
            <span
              className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[idea.status] || STATUS_COLORS.new}`}
            >
              {STATUS_LABELS[idea.status] || idea.status}
            </span>
          </div>
        </div>
        <p className="mt-1 line-clamp-2 text-sm text-gray-400">{idea.description}</p>
        <div className="mt-2 flex items-center gap-3 text-xs text-gray-500">
          <span>by {idea.authorName}</span>
          <span>{timeAgo(idea.createdAt)}</span>
          <span className="flex items-center gap-1">
            <MessageSquare size={12} /> {idea.commentCount}
          </span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Idea Detail View
// ---------------------------------------------------------------------------

function IdeaDetailView({
  ideaId,
  isAdmin,
  onBack,
}: {
  ideaId: string;
  isAdmin: boolean;
  onBack: () => void;
}) {
  const [idea, setIdea] = useState<IdeaDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);

  // Admin state
  const [adminStatus, setAdminStatus] = useState('');
  const [adminCategory, setAdminCategory] = useState('');
  const [adminResponse, setAdminResponse] = useState('');
  const [saving, setSaving] = useState(false);

  // Merge state
  const [showMerge, setShowMerge] = useState(false);
  const [mergeSearch, setMergeSearch] = useState('');
  const [mergeResults, setMergeResults] = useState<IdeaListItem[]>([]);
  const [merging, setMerging] = useState(false);

  // Merged ideas expand
  const [showMerged, setShowMerged] = useState(false);

  const loadIdea = useCallback(async () => {
    try {
      const { idea: detail } = await getIdeaDetail(ideaId);
      setIdea(detail);
      setAdminStatus(detail.status);
      setAdminCategory(detail.category);
      setAdminResponse(detail.adminResponse || '');
    } catch {
      // handle error
    } finally {
      setLoading(false);
    }
  }, [ideaId]);

  useEffect(() => {
    loadIdea();
  }, [loadIdea]);

  const handleRate = async (rating: number) => {
    try {
      await rateIdea(ideaId, rating);
      await loadIdea();
    } catch {
      // silently fail
    }
  };

  const handleAddComment = async () => {
    if (!commentText.trim()) return;
    setSubmittingComment(true);
    try {
      await addIdeaComment(ideaId, commentText.trim());
      setCommentText('');
      await loadIdea();
    } catch {
      // handle error
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleAdminUpdate = async () => {
    setSaving(true);
    try {
      const updates: Record<string, string> = {};
      if (adminStatus !== idea?.status) updates.status = adminStatus;
      if (adminCategory !== idea?.category) updates.category = adminCategory;
      if (adminResponse !== (idea?.adminResponse || '')) updates.adminResponse = adminResponse;

      if (Object.keys(updates).length > 0) {
        await updateIdea(ideaId, updates);
        await loadIdea();
      }
    } catch {
      // handle error
    } finally {
      setSaving(false);
    }
  };

  const handleMergeSearch = async () => {
    if (!mergeSearch.trim()) return;
    try {
      const { ideas } = await listIdeas({ search: mergeSearch });
      setMergeResults(ideas.filter((i) => i.id !== ideaId && !i.mergedIntoId));
    } catch {
      setMergeResults([]);
    }
  };

  const handleMerge = async (sourceId: string) => {
    if (!confirm('Merge this idea? Ratings will be transferred and comments moved.')) return;
    setMerging(true);
    try {
      await mergeIdea(ideaId, sourceId);
      setShowMerge(false);
      setMergeSearch('');
      setMergeResults([]);
      await loadIdea();
    } catch {
      // handle error
    } finally {
      setMerging(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-500">Loading...</div>
    );
  }

  if (!idea) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-500">Idea not found</div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back button + header */}
      <div>
        <button
          onClick={onBack}
          className="mb-4 flex items-center gap-1 text-sm text-gray-400 hover:text-gray-200"
        >
          <ArrowLeft size={16} /> Back to Ideas
        </button>

        <div className="flex items-start justify-between gap-4">
          <h2 className="text-2xl font-semibold">{idea.title}</h2>
          <div className="flex flex-shrink-0 gap-2">
            <span
              className={`rounded-full px-2.5 py-1 text-xs font-medium ${CATEGORY_COLORS[idea.category] || CATEGORY_COLORS.other}`}
            >
              {CATEGORY_LABELS[idea.category] || idea.category}
            </span>
            <span
              className={`rounded-full px-2.5 py-1 text-xs font-medium ${STATUS_COLORS[idea.status] || STATUS_COLORS.new}`}
            >
              {STATUS_LABELS[idea.status] || idea.status}
            </span>
          </div>
        </div>
        <p className="mt-1 text-sm text-gray-500">
          by {idea.authorName} · {timeAgo(idea.createdAt)}
        </p>
      </div>

      {/* Description */}
      <div className="rounded-lg border border-gray-800 bg-gray-900 p-6">
        <p className="whitespace-pre-wrap text-sm text-gray-300">{idea.description}</p>
      </div>

      {/* Rating section */}
      <div className="grid gap-6 sm:grid-cols-2">
        {/* Left: average + user rating */}
        <div className="rounded-lg border border-gray-800 bg-gray-900 p-6">
          <h4 className="mb-3 text-sm font-medium text-gray-400">Rating</h4>
          <div className="flex items-center gap-3">
            <StarRating value={idea.ratingAverage} size={24} />
            <span className="text-lg font-semibold text-gray-200">
              {idea.ratingAverage.toFixed(1)}
            </span>
            <span className="text-sm text-gray-500">{idea.ratingCount} ratings</span>
          </div>

          <div className="mt-4 border-t border-gray-800 pt-4">
            <h5 className="mb-2 text-xs font-medium text-gray-500">Your Rating</h5>
            <StarRating
              value={idea.myRating ?? 0}
              interactive
              onRate={handleRate}
              size={22}
            />
            {idea.myRating && (
              <p className="mt-1 text-xs text-gray-500">
                {RATING_LABELS[idea.myRating]}
              </p>
            )}
          </div>
        </div>

        {/* Right: distribution */}
        <div className="rounded-lg border border-gray-800 bg-gray-900 p-6">
          <h4 className="mb-3 text-sm font-medium text-gray-400">Rating Distribution</h4>
          <RatingDistribution
            distribution={idea.ratingDistribution}
            total={idea.ratingCount}
          />
        </div>
      </div>

      {/* Admin response */}
      {idea.adminResponse && (
        <div className="rounded-lg border-l-4 border-brand-500 bg-gray-800/50 p-4">
          <div className="mb-1 flex items-center gap-2 text-xs font-medium text-brand-400">
            <Shield size={14} /> Admin Response
          </div>
          <p className="text-sm text-gray-300">{idea.adminResponse}</p>
        </div>
      )}

      {/* Merged ideas */}
      {idea.mergedIdeas.length > 0 && (
        <div className="rounded-lg border border-gray-800 bg-gray-900 p-4">
          <button
            onClick={() => setShowMerged(!showMerged)}
            className="flex items-center gap-2 text-sm font-medium text-gray-400 hover:text-gray-200"
          >
            {showMerged ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            <GitMerge size={14} />
            Includes {idea.mergedIdeas.length} merged suggestion
            {idea.mergedIdeas.length !== 1 ? 's' : ''}
          </button>
          {showMerged && (
            <ul className="mt-2 space-y-1 pl-8">
              {idea.mergedIdeas.map((m) => (
                <li key={m.id} className="text-sm text-gray-500">
                  {m.title}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Admin controls */}
      {isAdmin && (
        <div className="rounded-lg border border-gray-800 bg-gray-900 p-6">
          <h4 className="mb-4 flex items-center gap-2 text-sm font-medium text-gray-300">
            <Shield size={16} /> Admin Controls
          </h4>

          <div className="space-y-4">
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="mb-1 block text-xs text-gray-500">Status</label>
                <select
                  value={adminStatus}
                  onChange={(e) => setAdminStatus(e.target.value)}
                  className="w-full rounded-md border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-100 focus:border-brand-500 focus:outline-none"
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {STATUS_LABELS[s]}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex-1">
                <label className="mb-1 block text-xs text-gray-500">Category</label>
                <select
                  value={adminCategory}
                  onChange={(e) => setAdminCategory(e.target.value)}
                  className="w-full rounded-md border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-100 focus:border-brand-500 focus:outline-none"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {CATEGORY_LABELS[c]}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="mb-1 block text-xs text-gray-500">Admin Response</label>
              <textarea
                value={adminResponse}
                onChange={(e) => setAdminResponse(e.target.value)}
                placeholder="Official response to this idea..."
                rows={3}
                className="w-full rounded-md border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-100 placeholder-gray-500 focus:border-brand-500 focus:outline-none"
              />
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={handleAdminUpdate}
                disabled={saving}
                className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Update'}
              </button>

              <button
                onClick={() => setShowMerge(!showMerge)}
                className="flex items-center gap-1 rounded-md border border-gray-700 px-3 py-2 text-sm text-gray-300 hover:bg-gray-800"
              >
                <GitMerge size={14} /> Merge Another Idea
              </button>
            </div>

            {/* Merge picker */}
            {showMerge && (
              <div className="rounded-md border border-gray-700 bg-gray-800 p-4">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={mergeSearch}
                    onChange={(e) => setMergeSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleMergeSearch()}
                    placeholder="Search ideas to merge..."
                    className="flex-1 rounded-md border border-gray-600 bg-gray-900 px-3 py-1.5 text-sm text-gray-100 placeholder-gray-500 focus:border-brand-500 focus:outline-none"
                  />
                  <button
                    onClick={handleMergeSearch}
                    className="rounded-md bg-gray-700 px-3 py-1.5 text-sm text-gray-200 hover:bg-gray-600"
                  >
                    Search
                  </button>
                </div>
                {mergeResults.length > 0 && (
                  <ul className="mt-2 max-h-40 space-y-1 overflow-y-auto">
                    {mergeResults.map((r) => (
                      <li
                        key={r.id}
                        className="flex items-center justify-between rounded-md px-3 py-2 text-sm hover:bg-gray-700"
                      >
                        <span className="text-gray-300">{r.title}</span>
                        <button
                          onClick={() => handleMerge(r.id)}
                          disabled={merging}
                          className="text-xs text-brand-400 hover:text-brand-300 disabled:opacity-50"
                        >
                          Merge
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Comments section */}
      <div className="rounded-lg border border-gray-800 bg-gray-900 p-6">
        <h4 className="mb-4 flex items-center gap-2 text-sm font-medium text-gray-300">
          <MessageSquare size={16} /> Discussion ({idea.comments.length} comments)
        </h4>

        {idea.comments.length > 0 ? (
          <div className="space-y-4">
            {idea.comments.map((comment) => (
              <div
                key={comment.id}
                className={`rounded-md p-3 ${
                  comment.isSystem
                    ? 'border border-gray-800 bg-gray-950'
                    : 'border border-gray-800 bg-gray-800/50'
                }`}
              >
                <div className="mb-1 flex items-center gap-2 text-xs text-gray-500">
                  {comment.isSystem ? (
                    <Info size={12} className="text-gray-600" />
                  ) : null}
                  <span className={comment.isSystem ? 'italic text-gray-600' : 'font-medium text-gray-400'}>
                    {comment.userName}
                  </span>
                  <span>{timeAgo(comment.createdAt)}</span>
                </div>
                <p
                  className={`text-sm ${
                    comment.isSystem ? 'italic text-gray-500' : 'text-gray-300'
                  }`}
                >
                  {comment.text}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <p className="mb-4 text-sm text-gray-500">No comments yet.</p>
        )}

        {/* Add comment */}
        <div className="mt-4 border-t border-gray-800 pt-4">
          <textarea
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            placeholder="Share a use case, add context, or discuss this idea..."
            rows={3}
            className="w-full rounded-md border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-100 placeholder-gray-500 focus:border-brand-500 focus:outline-none"
          />
          <div className="mt-2 flex justify-end">
            <button
              onClick={handleAddComment}
              disabled={submittingComment || !commentText.trim()}
              className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {submittingComment ? 'Posting...' : 'Post Comment'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export function IdeasPage() {
  const { user } = useAuth();
  const isAdmin = !!user && ['owner', 'admin'].includes(user.role);

  // View state
  const [view, setView] = useState<'list' | 'detail'>('list');
  const [selectedIdeaId, setSelectedIdeaId] = useState<string | null>(null);

  // List state
  const [ideas, setIdeas] = useState<IdeaListItem[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [sortBy, setSortBy] = useState('newest');

  // Modal
  const [showSubmitModal, setShowSubmitModal] = useState(false);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const loadIdeas = useCallback(async () => {
    try {
      const { ideas: list } = await listIdeas({
        status: filterStatus || undefined,
        category: filterCategory || undefined,
        sort: sortBy,
        search: debouncedSearch || undefined,
      });
      setIdeas(list);
    } catch {
      // handle error
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterCategory, sortBy, debouncedSearch]);

  useEffect(() => {
    loadIdeas();
  }, [loadIdeas]);

  const handleRate = async (ideaId: string, rating: number) => {
    try {
      await rateIdea(ideaId, rating);
      // Optimistic: reload list
      await loadIdeas();
    } catch {
      // silently fail
    }
  };

  const handleSelectIdea = (id: string) => {
    setSelectedIdeaId(id);
    setView('detail');
  };

  const handleBack = () => {
    setView('list');
    setSelectedIdeaId(null);
    loadIdeas();
  };

  const handleSubmitIdea = async (data: {
    title: string;
    description: string;
    category: string;
  }) => {
    await createIdea(data);
    setShowSubmitModal(false);
    await loadIdeas();
  };

  const hasActiveFilters = filterCategory || filterStatus || debouncedSearch;

  if (view === 'detail' && selectedIdeaId) {
    return (
      <div className="mx-auto max-w-7xl">
        <IdeaDetailView ideaId={selectedIdeaId} isAdmin={isAdmin} onBack={handleBack} />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Ideas & Feature Requests</h2>
          <p className="mt-1 text-gray-400">Share and vote on ideas for OpenQuote Pro</p>
        </div>
        <button
          onClick={() => setShowSubmitModal(true)}
          className="flex items-center gap-2 rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
        >
          <Plus size={16} /> Submit an Idea
        </button>
      </div>

      {/* Filter bar */}
      <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-gray-800 bg-gray-900 px-4 py-3">
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="rounded-md border border-gray-700 bg-gray-800 px-2 py-1.5 text-xs text-gray-200 focus:border-brand-500 focus:outline-none"
        >
          <option value="">All Categories</option>
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {CATEGORY_LABELS[c]}
            </option>
          ))}
        </select>

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="rounded-md border border-gray-700 bg-gray-800 px-2 py-1.5 text-xs text-gray-200 focus:border-brand-500 focus:outline-none"
        >
          <option value="">All Statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {STATUS_LABELS[s]}
            </option>
          ))}
        </select>

        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="rounded-md border border-gray-700 bg-gray-800 px-2 py-1.5 text-xs text-gray-200 focus:border-brand-500 focus:outline-none"
        >
          <option value="newest">Newest</option>
          <option value="top-rated">Top Rated</option>
          <option value="recently-updated">Recently Updated</option>
        </select>

        <div className="relative min-w-[160px] flex-1">
          <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search ideas..."
            className="w-full rounded-md border border-gray-700 bg-gray-800 py-1.5 pl-7 pr-3 text-xs text-gray-200 placeholder-gray-500 focus:border-brand-500 focus:outline-none"
          />
        </div>

        {hasActiveFilters && (
          <button
            onClick={() => {
              setFilterCategory('');
              setFilterStatus('');
              setSearchQuery('');
              setDebouncedSearch('');
            }}
            className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-300"
          >
            <X className="h-3 w-3" /> Clear
          </button>
        )}
      </div>

      {/* Ideas list */}
      {loading ? (
        <div className="flex items-center justify-center py-16 text-gray-500">
          Loading ideas...
        </div>
      ) : ideas.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-gray-800 bg-gray-900 py-16">
          <Star size={40} className="mb-3 text-gray-700" />
          <p className="text-gray-500">
            {hasActiveFilters
              ? 'No ideas match your filters.'
              : 'No ideas yet. Be the first to submit a feature request!'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {ideas.map((idea) => (
            <IdeaCard
              key={idea.id}
              idea={idea}
              onSelect={handleSelectIdea}
              onRate={handleRate}
            />
          ))}
        </div>
      )}

      {/* Submit modal */}
      {showSubmitModal && (
        <SubmitIdeaModal
          onClose={() => setShowSubmitModal(false)}
          onSubmit={handleSubmitIdea}
        />
      )}
    </div>
  );
}
